import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, Grid, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { playerRequest } from '../service'
import { normalizePlayerPosition, PLAYER_POSITION_OPTIONS } from '../constants/playerPositions'

const blank = { name: '', birth_year: '', position: '', team: '', jersey: '', charter: '' }
const labels = { goal: 'Development goal', observation: 'Observation', coach_feedback: 'Coach feedback (entered by you)', correction: 'Correction' }

export default function Players () {
  const [players, setPlayers] = useState([])
  const [selected, setSelected] = useState(null)
  const [form, setForm] = useState(blank)
  const [history, setHistory] = useState([])
  const [kind, setKind] = useState('goal')
  const [note, setNote] = useState('')
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [saved, setSaved] = useState('')
  const refresh = () => playerRequest('/players').then(setPlayers)
  useEffect(() => { refresh().catch(e => setError(e.message)).finally(() => setLoaded(true)) }, [])
  useEffect(() => {
    let active = true
    setHistory([])
    setNote('')
    if (selected) playerRequest(`/players/${selected.id}/history`).then(rows => { if (active) setHistory(rows) }).catch(e => { if (active) setError(e.message) })
    return () => { active = false }
  }, [selected?.id])
  const choose = player => {
    setSelected(player)
    setForm(player ? Object.fromEntries(Object.keys(blank).map(key => [key, player[key] ?? ''])) : blank)
    setSaved('')
    setError('')
  }
  const save = async () => {
    setBusy(true); setError(''); setSaved('')
    try {
      const body = { ...form, position: normalizePlayerPosition(form.position), birth_year: form.birth_year === '' ? null : Number(form.birth_year) }
      if (selected) body.expected_revision = selected.revision
      const player = await playerRequest(selected ? `/players/${selected.id}` : '/players', selected ? 'PATCH' : 'POST', body)
      await refresh(); choose(player); setSaved('Player saved.')
    } catch (e) { setError(e.message) } finally { setBusy(false) }
  }
  const addNote = async () => {
    setBusy(true); setError('')
    try {
      await playerRequest(`/players/${selected.id}/history`, 'POST', { kind, content: note, observed_at: `${date}T12:00:00Z` })
      setHistory(await playerRequest(`/players/${selected.id}/history`)); setNote('')
    } catch (e) { setError(e.message) } finally { setBusy(false) }
  }
  const withdraw = async id => {
    setBusy(true); setError('')
    try { await playerRequest(`/players/${selected.id}/history/${id}`, 'DELETE'); setHistory(rows => rows.filter(row => row.id !== id)) } catch (e) { setError(e.message) } finally { setBusy(false) }
  }
  return <Stack spacing={2} sx={{ maxWidth: 1100, mx: 'auto', py: 2 }}>
    <Typography variant="h4">Players</Typography>
    <Typography color="text.secondary">Manage each player in your family. Profiles and development history are private to your account.</Typography>
    {error && <Alert severity="error">{error}</Alert>}
    {saved && <Alert severity="success">{saved}</Alert>}
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {players.map(player => <Button key={player.id} disabled={busy} variant={selected?.id === player.id ? 'contained' : 'outlined'} onClick={() => choose(player)}>{player.name}</Button>)}
      <Button disabled={busy || !loaded} onClick={() => choose(null)}>Add player</Button>
    </Stack>
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6">{selected ? 'Player profile' : 'New player'}</Typography>
        <Grid container spacing={2}>
          {Object.entries({ name: 'Player name', birth_year: 'Birth year (optional)', position: 'Position', team: 'Current team', jersey: 'Usual jersey number' }).map(([key, label]) => <Grid item xs={12} sm={6} key={key}>
            {key === 'position'
              ? <TextField select fullWidth label={label} value={normalizePlayerPosition(form.position)} disabled={busy} helperText="Choose the closest match for this player." onChange={e => setForm(value => ({ ...value, position: e.target.value }))}>
                  <MenuItem value="">Not set</MenuItem>
                  {PLAYER_POSITION_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </TextField>
              : <TextField fullWidth label={label} required={key === 'name'} type={key === 'birth_year' ? 'number' : 'text'} value={form[key]} disabled={busy} onChange={e => setForm(value => ({ ...value, [key]: e.target.value }))} />}
          </Grid>)}
        </Grid>
        <TextField label="Development charter" helperText="Your priorities and preferences for this player. Record dated goals and observations below." multiline minRows={3} value={form.charter} disabled={busy} onChange={e => setForm(value => ({ ...value, charter: e.target.value }))} />
        <Box><Button variant="contained" onClick={save} disabled={busy || !loaded || !form.name.trim()}>Save player</Button></Box>
      </Stack>
    </Paper>
    {selected && <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Player history</Typography>
        <Alert severity="info">Saved notes persist between games. AI coaching (an admin-only beta) uses this player’s profile, development charter, goals and recent observations, coach feedback and corrections as they are when a coaching run starts; each report shows what it used. A note you withdraw is not sent to the AI after that: a queued run that included it stops and asks you to start again, and coaching that finishes after the withdrawal is not saved. Earlier reports are not changed.</Alert>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField select label="Entry type" value={kind} onChange={e => setKind(e.target.value)} fullWidth disabled={busy}>{Object.entries(labels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField>
          <TextField label="Observation date" type="date" value={date} InputLabelProps={{ shrink: true }} onChange={e => setDate(e.target.value)} disabled={busy} />
        </Stack>
        <TextField label="History note" multiline minRows={2} value={note} onChange={e => setNote(e.target.value)} disabled={busy} />
        <Box><Button onClick={addNote} disabled={busy || !note.trim() || !date}>Save history note</Button></Box>
        {!history.length && <Typography color="text.secondary">No history entries yet.</Typography>}
        {history.map(entry => <Paper key={entry.id} variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2">{labels[entry.kind]} · {new Date(entry.observed_at).toLocaleDateString()}</Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{entry.content}</Typography>
          <Typography variant="caption" color="text.secondary">Source: entered by you</Typography>
          <Box><Button size="small" disabled={busy} onClick={() => withdraw(entry.id)}>Withdraw entry</Button></Box>
        </Paper>)}
      </Stack>
    </Paper>}
    <Button component={RouterLink} to="/new">Upload a game</Button>
  </Stack>
}
